import React from 'react';

import bp from 'styled-components-breakpoint';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { px2rem } from '../../utilities/UnitHelper';
import { Column, Row } from '../Grid';
import SocialButton from '../SocialButton';
import Link from '../Link';
import BlackWheel from '../Icons/SVG/BlackWheel';
import SCWheelLogo from './sc-wheel-logo.svg';
import Input from '../Input/input';
import Button from '../Button';
import { ChevronRight } from '../Icons';

const RightColumn = styled(Column)`
  display: none;
  min-height: 12rem;
  position: relative;
  ${bp('desktopLarge')`
    display: block;
    min-height: 18rem;
  `};
`;

const CopyRightUnderMenu = styled(RightColumn)`
  display: block;
  ${bp('desktopLarge')`
    display: none;
  `};
`;

const StyledRightColumn = styled(Column)`
  width: 100%;
  ${bp('desktopLarge')`
    width: 24rem;
  `};

  &.show-email {
    ${bp('desktopLarge')`
      width: 32rem;
    `};
    ${bp('desktopWide')`
      width: 35rem;
    `};
  }
`;

const StyledHeader = styled.h4`
  ${(props) => props.theme.typography.subHeaderCaps}
  color: ${(props) => props.theme.colors.grayscale.gray0};
  font-size: 0.75rem;
  letter-spacing: 1.7px;
  margin: 0 0 ${px2rem(16)};
`;

const StyledList = styled.ul`
  ${(props) => props.theme.typography.bodySmall}
  flex: 1;
  list-style-type: none;
  margin-bottom: 3.5rem;
  padding-left: 0;

  li {
    ${(props) => props.theme.typography.bodySmall};
    margin: ${px2rem(16)} 0;
    padding-left: 0;

    &:last-child {
      margin-bottom: 0;
    }

    a {
      ${(props) => props.theme.typography.bodySmall};
      font-size: 0.75rem;
      line-height: 1rem;
      color: ${(props) => props.theme.colors.grayscale.black};
    }
  }
`;

const StyledLink = styled(Link)`
  :hover {
    text-decoration: underline;
  }
`;

const BorderDiv = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.grayscale.gray3};
  margin: 2.0625rem 0;
  width: 100%;
  ${bp('desktopLarge')`
    display: none;
  `};
`;

const StyledEmailColumn = styled(Column)`
  position: relative;

  #alert-triangle {
    right: 2.5rem;
  }

  ${bp('tablet')`
    padding-left: 0.5rem;
    text-align: right;
    width: 23rem;
  `};
  ${bp('desktopLarge')`
    margin-top: 1rem;
    width: 22rem;
  `};
  ${bp('desktopWide')`
    margin-top: 1rem;
    width: 23rem;
  `};
`;

const FollowContent = styled.div`
  margin-top: 1.65rem;
  ${bp('tablet')`
    max-width: 9.25rem;
  `};
`;

const StyledFollowPText = styled.p`
  ${(props) => props.theme.typography.headlineCaps};
  color: ${(props) => props.theme.colors.grayscale.gray0};
  letter-spacing: 1.7px;
  line-height: 1;
  font-size: 0.75rem;
  margin-top: 1.65rem;
  text-align: left;
  width: 100%;
`;

const StyledSocialButtons = styled(Row)`
  a:first-child {
    margin-left: 0.65rem;
  }
`;

const StyledCopyColumn = styled(Column)`
  align-items: flex-end;
  display: flex;

  .copyright {
    ${(props) => props.theme.typography.bodySmall};
    flex: 1;
    text-align: left;

    p {
      line-height: 0.875rem;
      margin-top: 0;
      margin-bottom: 0;
      width: 90%;
      ${bp('desktopLarge')`
        margin-top: 1rem;
      `};

      &.legal {
        ${(props) => props.theme.typography.bodySmall};
        font-size: 0.625rem;
      }

      &:first-child {
        ${(props) => props.theme.typography.bodyBold};
        font-size: 0.75rem;
      }
    }
  }

  ${bp('desktopLarge')`{
    margin-top: 1.25rem;
  `};
`;

const StyledWheelIcon = styled(BlackWheel)`
  height: ${px2rem(60)};
  width: ${px2rem(60)};
  ${bp('desktopLarge')`
    height: 3.75rem;
    width: 3.75rem;
  `};
`;

const StyledSocialButton = styled(SocialButton)`
  margin-left: 1.75rem;
`;

const StyledFollowContent = styled(Column)`
  ${bp('tablet')`
    width: 12rem;
  `}

  ${bp('desktopLarge')`
    margin-top: 1rem;
    width: 10rem;
  `}

  ${bp('desktopWide')`
    width: 12rem;
  `}
`;

const StyledInput = styled(Input)`
  border: white;
`;

const StyledEmailButton = styled(Button)`
  height: 2.125rem;
  padding: 0 1rem;
  position: absolute;
  right: 1.25rem;
  top: 3.875rem;
  width: 1rem;
  z-index: 1;
`;

const StyledRowContent = styled(Row)`
  flex-direction: column-reverse;
  ${bp('desktopLarge')`
      flex-direction: initial;
      justify-content: space-between;
  `};

  .svg-block {
    svg {
      width: 13.875rem;
      height: 100%;
      padding-left: 0.5rem;
      ${bp('desktopLarge')`
        width: 23rem;
      `}
    }
  }
`;

const leftColumn = {
  mobile: 1,
  tablet: 1,
  desktopLarge: 1 / 2,
};

const rightColumn = {
  mobile: 1,
  tablet: 1,
  desktopLarge: 1 / 2,
};

const rightSecondColumn = {
  mobile: 1,
  tablet: 1,
  desktopLarge: 1,
};

const styledFollowColumn = {
  mobile: 1,
  tablet: 1,
  desktop: 1 / 2,
};

const styledEmailListColumn = {
  mobile: 1,
  tablet: 1,
  desktop: 1 / 2,
};

function MailingBox({
  showMailingBox,
  onSubscriptionClick,
  emailPlaceHolder,
  emailInputName,
  emailValue,
  onChangeHandlerEmail,
  emailErrorMessage,
  emailSuccessMessage,
}) {
  return (
    showMailingBox && (
      <StyledEmailColumn width={styledEmailListColumn}>
        <StyledFollowPText>BE THE FIRST TO KNOW</StyledFollowPText>
        <StyledEmailButton
          variation="primary"
          icon={ChevronRight}
          onClick={onSubscriptionClick}
        />
        <StyledInput
          autoComplete="on"
          tabIndex={0}
          id="email"
          type="email"
          placeholder={emailPlaceHolder}
          name={emailInputName}
          value={emailValue}
          onChangeHandler={onChangeHandlerEmail}
          maxLength={100}
          errorMessage={emailErrorMessage}
          successMessage={emailSuccessMessage}
        />
      </StyledEmailColumn>
    )
  );
}

/**
 * propTypes for this MailingBox
 */
MailingBox.propTypes = {
  /** String email place holder */
  emailPlaceHolder: PropTypes.string,
  /** String email input name */
  emailInputName: PropTypes.string,
  /** String email input value */
  emailValue: PropTypes.string,
  /** Func to handler the on change event of the email input */
  onChangeHandlerEmail: PropTypes.func,
  /** errorMessage passed down from the parent */
  emailErrorMessage: PropTypes.string,
  /** successMessage passed down from the parent */
  emailSuccessMessage: PropTypes.string,
  /** Func to handler the on subscription event */
  onSubscriptionClick: PropTypes.func,
  /** bool show mailing box */
  showMailingBox: PropTypes.bool,
};

/**
 * Default prop types
 */
MailingBox.defaultProps = {
  emailPlaceHolder: 'Your email address',
  emailInputName: 'email',
  emailValue: '',
  emailErrorMessage: '',
  emailSuccessMessage: '',
  onChangeHandlerEmail: null,
  onSubscriptionClick: null,
  showMailingBox: false,
};

/**
 *  SC Footer component with a links lists, social buttons and logo.
 */
function Footer({
  bannerAlt,
  bannerImage,
  bannerLink,
  items,
  socials,
  followUsText,
  LinkWrapper,
  emailPlaceHolder,
  emailInputName,
  emailValue,
  onChangeHandlerEmail,
  emailErrorMessage,
  emailSuccessMessage,
  onSubscriptionClick,
  showMailingBox,
  ...rest
}) {
  const data = Object.keys(items).map((listHeader) => (
    <Column key={`footer-column-${listHeader}`} width={1 / 3} my={0}>
      <StyledHeader key={`footer-${listHeader}`}>{listHeader}</StyledHeader>
      <StyledList>
        {items[listHeader].map((link) => (
          <li key={`footer-${link.id}`}>
            <StyledLink
              id={`footer-${link.id}`}
              href={link.url}
              LinkWrapper={LinkWrapper}
              target={link.target}
              rel={link.rel}
            >
              {link.name}
            </StyledLink>
          </li>
        ))}
      </StyledList>
    </Column>
  ));

  const legalCopyright = `Copyright © ${new Date().getFullYear()} SoulCycle`;
  const legalText =
    // eslint-disable-next-line max-len
    'SoulCycle Inc. is a corporation registered in Delaware, USA with its headquarters at 609 Greenwich Street, New York, NY 10014';

  const socialButtons = socials.map((name) => {
    const id = name.replace(/ /, '-');
    const filled = !(name === 'instagram');

    return (
      <StyledSocialButton
        aria-label={name}
        id={id}
        key={`footer-${name}`}
        type={name}
        filled={filled}
      />
    );
  });

  return (
    <div {...rest}>
      <StyledRowContent>
        <CopyRightUnderMenu width={rightColumn}>
          <Row>
            <StyledCopyColumn>
              <div className="copyright">
                <p>{legalCopyright}</p>
                <p className="legal">{legalText}</p>
              </div>
              <StyledWheelIcon />
            </StyledCopyColumn>
          </Row>
        </CopyRightUnderMenu>
        <BorderDiv />
        <Column width={leftColumn}>
          <Row>{data}</Row>
        </Column>
        <BorderDiv />
        <StyledRightColumn className={showMailingBox ? 'show-email' : ''}>
          <Row>
            <div className="svg-block">
              <SCWheelLogo />
            </div>
          </Row>
          <Row>
            <StyledFollowContent width={styledFollowColumn}>
              <FollowContent>
                <StyledFollowPText>{followUsText}</StyledFollowPText>
                <StyledSocialButtons>{socialButtons}</StyledSocialButtons>
              </FollowContent>
            </StyledFollowContent>
            <MailingBox
              showMailingBox={showMailingBox}
              emailErrorMessage={emailErrorMessage}
              emailSuccessMessage={emailSuccessMessage}
              emailInputName={emailInputName}
              emailPlaceHolder={emailPlaceHolder}
              emailValue={emailValue}
              onChangeHandlerEmail={onChangeHandlerEmail}
              onSubscriptionClick={onSubscriptionClick}
            />
          </Row>
          <Row>
            <RightColumn width={rightSecondColumn}>
              <Row>
                <StyledCopyColumn>
                  <div className="copyright">
                    <p>{legalCopyright}</p>
                    <p className="legal">{legalText}</p>
                  </div>
                  <StyledWheelIcon />
                </StyledCopyColumn>
              </Row>
            </RightColumn>
          </Row>
        </StyledRightColumn>
      </StyledRowContent>
    </div>
  );
}

/**
 * propTypes for the Footer Component
 */
Footer.propTypes = {
  /** banner alt text */
  bannerAlt: PropTypes.string,
  /** banner image src  */
  bannerImage: PropTypes.string,
  /** banner link url */
  bannerLink: PropTypes.string,
  /** object of arrays of objects that contain the links of the footer.  each link item requires an id, name, and url */
  items: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      })
    )
  ).isRequired,
  /** Array of string that contains the the types of social Buttons */
  socials: PropTypes.arrayOf(SocialButton.propTypes.type),
  /** String for the Social Links' Header */
  followUsText: PropTypes.string,
  /**
   * Wrapper for Next.js Link or React Router Link
   */
  LinkWrapper: PropTypes.func,
  /** String email place holder */
  emailPlaceHolder: PropTypes.string,
  /** String email input name */
  emailInputName: PropTypes.string,
  /** String email input value */
  emailValue: PropTypes.string,
  /** Func to handler the on change event of the email input */
  onChangeHandlerEmail: PropTypes.func,
  /** errorMessage passed down from the parent */
  emailErrorMessage: PropTypes.string,
  /** successMessage passed down from the parent */
  emailSuccessMessage: PropTypes.string,
  /** Func to handler the on subscription event */
  onSubscriptionClick: PropTypes.func,
  /** bool show mailing box */
  showMailingBox: PropTypes.bool,
  /** rel attribute for links  */
  rel: PropTypes.string,
};

/**
 * Default props
 */
Footer.defaultProps = {
  bannerAlt: 'Download our app available in the App Store!',
  bannerImage:
    'https://scs-media.soul-cycle.com/Download_on_the_App_Store_Badge.svg',
  bannerLink:
    'https://itunes.apple.com/us/app/soulcycle-indoor-cycling/id966733747?mt=8',
  followUsText: 'TAP IT BACK',
  LinkWrapper: null,
  socials: ['instagram', 'facebook', 'twitter'],
  emailPlaceHolder: 'Your email address',
  emailInputName: 'email',
  emailValue: '',
  emailErrorMessage: '',
  emailSuccessMessage: '',
  onChangeHandlerEmail: null,
  onSubscriptionClick: null,
  showMailingBox: false,
  rel: '',
};
/* @component */
export default Footer;
